/**
 * Profilo provvigioni del partner (riga `referrers` collegata via
 * `external_partner_id`). Partner e segnalatori sono un'unica anagrafica:
 * ogni partner ha il suo profilo segnalatore, che e' quello letto dal calcolo
 * provvigioni (resolve_commission_rate, deal_referrers). Qui solo funzioni pure:
 * nessun accesso al DB.
 */

/** Campi del partner usati per costruire/sincronizzare il profilo segnalatore. */
export interface PartnerAnagraphic {
  id: string;
  name: string;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  tax_code?: string | null;
  vat_number?: string | null;
  commission_rate?: number | null;
}

/** Sottoinsieme del profilo segnalatore usato da tabella e scheda partner. */
export interface ReferrerProfileLite {
  id: string;
  external_partner_id?: string | null;
  commission_rate?: number | null;
  commission_type?: string | null;
  commission_fixed_amount?: number | null;
  referrer_group_id?: string | null;
  created_at?: string | null;
  /** tag del catalogo lead/clienti (client_tags) assegnati al segnalatore */
  tag_ids?: string[] | null;
}

/** Percentuale del partner se valida (0-100), altrimenti 0. Mai un 10% inventato. */
export function initialCommissionRate(rate: number | null | undefined): number {
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return 0;
  if (rate <= 0) return 0;
  return Math.min(rate, 100);
}

/** Dati anagrafici che il partner porta sul profilo segnalatore (mai la provvigione). */
export function buildReferrerSyncFromPartner(partner: PartnerAnagraphic) {
  return {
    full_name: partner.name,
    email: partner.email || null,
    phone: partner.phone || null,
    tax_code: partner.tax_code || null,
    vat_number: partner.vat_number || null,
  };
}

/** Riga `referrers` da inserire per un partner che non ha ancora il profilo. */
export function buildReferrerFromPartner(
  partner: PartnerAnagraphic,
  organizationId: string,
  createdBy?: string | null,
) {
  return {
    ...buildReferrerSyncFromPartner(partner),
    organization_id: organizationId,
    external_partner_id: partner.id,
    commission_rate: initialCommissionRate(partner.commission_rate),
    commission_type: 'percentage' as const,
    notes: `Profilo provvigioni del partner ${partner.company || partner.name}`,
    created_by: createdBy || null,
  };
}

/**
 * Un partner dovrebbe avere un solo profilo, ma in passato ne sono nati due:
 * si usa il piu' vecchio (quello con lo storico delle offerte).
 */
export function pickProfilesByPartner<T extends ReferrerProfileLite>(profiles: T[]): Map<string, T> {
  const map = new Map<string, T>();
  for (const p of profiles) {
    const pid = p.external_partner_id;
    if (!pid) continue;
    const current = map.get(pid);
    if (!current || (p.created_at || '') < (current.created_at || '')) map.set(pid, p);
  }
  return map;
}

/** Testo della provvigione: "12%", "€ 50,00" oppure null se non impostata. */
export function formatReferrerCommission(profile: ReferrerProfileLite | null | undefined): string | null {
  if (!profile) return null;
  if (profile.commission_type === 'fixed') {
    const amount = Number(profile.commission_fixed_amount ?? 0);
    return `€ ${amount.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  const rate = Number(profile.commission_rate ?? 0);
  return `${rate.toLocaleString('it-IT', { maximumFractionDigits: 2 })}%`;
}

/** Valore numerico per ordinare la colonna Provvigione (i fissi dopo le percentuali). */
export function commissionSortValue(profile: ReferrerProfileLite | null | undefined): number | null {
  if (!profile) return null;
  if (profile.commission_type === 'fixed') return 1000 + Number(profile.commission_fixed_amount ?? 0);
  return Number(profile.commission_rate ?? 0);
}

/** Conta i clienti/lead per partner a partire da `clients.referrer_id`. */
export function countClientsByPartner(
  clientReferrerIds: (string | null | undefined)[],
  partnerByReferrerId: Map<string, string>,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const rid of clientReferrerIds) {
    if (!rid) continue;
    const pid = partnerByReferrerId.get(rid);
    if (!pid) continue;
    counts.set(pid, (counts.get(pid) || 0) + 1);
  }
  return counts;
}

/** Tag per partner: unione dei tag di tutti i suoi profili (anche gli eventuali doppioni). */
export function tagIdsByPartner(profiles: ReferrerProfileLite[]): Map<string, string[]> {
  const sets = new Map<string, Set<string>>();
  for (const p of profiles) {
    if (!p.external_partner_id || !p.tag_ids?.length) continue;
    const set = sets.get(p.external_partner_id) || new Set<string>();
    p.tag_ids.forEach((id) => set.add(id));
    sets.set(p.external_partner_id, set);
  }
  return new Map(Array.from(sets, ([pid, set]) => [pid, Array.from(set)]));
}

export type CommissionKind = 'percentage' | 'fixed';

/**
 * Valida il valore scritto nella scheda partner e prepara l'aggiornamento del
 * profilo. Percentuale 0-100; importo fisso >= 0 (la percentuale resta com'e',
 * il calcolo usa l'importo quando il tipo e' "fisso").
 */
export function buildCommissionUpdate(
  kind: CommissionKind,
  raw: string,
): { update: Record<string, unknown> } | { error: string } {
  const value = Number(String(raw).trim().replace(',', '.'));
  if (String(raw).trim() === '' || !Number.isFinite(value)) return { error: 'Inserisci un numero' };
  if (value < 0) return { error: 'Il valore non può essere negativo' };
  if (kind === 'percentage') {
    if (value > 100) return { error: 'La percentuale deve essere tra 0 e 100' };
    return { update: { commission_type: 'percentage', commission_rate: value, commission_fixed_amount: null } };
  }
  return { update: { commission_type: 'fixed', commission_fixed_amount: value } };
}
